import { UrgentDocsCard } from '../components/crm/UrgentDocsCard';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AlertsPanel from '../components/AlertsPanel';
import LegalChatAssistant from '../components/LegalChatAssistant';
import { InvitationBadge } from '../components/CollaborationPanel';
import OfflineIndicator from '../components/OfflineIndicator';
import { useOfflineContracts } from '../hooks/useOfflineSync';
import DashboardAnalytics from '../components/DashboardAnalytics';
import PersonalizedRecommendations from '../components/PersonalizedRecommendations';
import UsageDashboard from '../components/UsageDashboard';
import SubscriptionPlans from '../components/SubscriptionPlans';
import ContractImport from '../components/ContractImport';
import DeadlinesWidget from '../components/DeadlinesWidget';
import EnhancedDashboardCharts from '../components/EnhancedDashboardCharts';
import ThemeToggle from '../components/ThemeToggle';
import NotificationSettingsPanel from '../components/NotificationSettingsPanel';
import { CountrySelector, ProfileSelector } from '../components/country';
import ProfileHubsSection from '../components/country/ProfileHubsSection';
import { Brain, ArrowRight, ShieldCheck } from 'lucide-react';
import {
  Shield,
  Plus,
  FileText,
  Newspaper,
  LogOut,
  Clock,
  CheckCircle2,
  AlertCircle,
  LayoutTemplate,
  Loader2,
  PenTool,
  Bell,
  Sparkles,
  Users,
  CloudOff,
  Crown,
  Upload,
  Gift,
  HelpCircle,
  BarChart3,
  Settings,
  Calculator,
  Scale,
  Briefcase,
  Gavel,
  BookOpen,
  Building2,
  ArrowLeftRight,
  History
} from 'lucide-react';

const DashboardPage = () => {
  const { user, logout, logoutError, api } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAlerts, setShowAlerts] = useState(false);
  const [alertSummary, setAlertSummary] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showEnhancedCharts, setShowEnhancedCharts] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({ code: 'chile', name: 'Chile', flag: '🇨🇱' });
  const [selectedProfile, setSelectedProfile] = useState(null);
  
  // Use offline-aware contract fetching
  const { 
    contracts: offlineContracts, 
    isLoading: offlineLoading, 
    source: offlineSource 
  } = useOfflineContracts();

  useEffect(() => {
    // Use offline-aware data when available
    if (!offlineLoading) {
      setContracts(offlineContracts);
      setDataSource(offlineSource);
      setLoading(false);
    }
  }, [offlineContracts, offlineLoading, offlineSource]);

  useEffect(() => {
    fetchAlertSummary();
    fetchDashboardStats();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await api.get('/contracts');
      setContracts(response.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Error al cargar contratos');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertSummary = async () => {
    try {
      const response = await api.get('/alerts/summary');
      setAlertSummary(response.data);
    } catch (error) {
      console.error('Error fetching alert summary:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/analytics/dashboard');
      setDashboardStats(response.data?.stats);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const handleLogout = async () => {
    if (await logout()) {
      navigate('/');
      toast.success('Sesión cerrada');
    }
  };

  const getStatusBadge = (contract) => {
    if (contract.is_signed) {
      return <span className="status-success flex items-center space-x-1"><CheckCircle2 className="w-3 h-3" /><span>Firmado</span></span>;
    }
    if (contract.status === 'borrador') {
      return <span className="status-neutral flex items-center space-x-1"><Clock className="w-3 h-3" /><span>Borrador</span></span>;
    }
    return <span className="status-warning flex items-center space-x-1"><AlertCircle className="w-3 h-3" /><span>{contract.status}</span></span>;
  };

  const getConflictScoreBadge = (score) => {
    if (score === null || score === undefined) return null;
    if (score < 30) return <span className="status-success">Riesgo: {score}</span>;
    if (score < 60) return <span className="status-warning">Riesgo: {score}</span>;
    return <span className="status-error">Riesgo: {score}</span>;
  };

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Alerts Panel */}
      {showAlerts && <AlertsPanel onClose={() => { setShowAlerts(false); fetchAlertSummary(); }} />}
      
      {/* Notification Settings Modal */}
      {showNotificationSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg bg-slate-900 rounded-2xl border border-slate-700 p-6 max-h-[90vh] overflow-y-auto">
            <NotificationSettingsPanel onClose={() => setShowNotificationSettings(false)} />
          </div>
        </div>
      )}
      
      {/* Subscription Plans Modal */}
      {showPlans && <SubscriptionPlans onClose={() => setShowPlans(false)} />}

      {/* Import Modal */}
      {showImport && (
        <ContractImport
          onClose={() => setShowImport(false)}
          onImported={(contract) => {
            setContracts(prev => [contract, ...prev]);
            setShowImport(false);
          }}
        />
      )}

      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-blue-500" />
              <span className="text-xl font-bold font-['Outfit']">NotaryOS</span>
              {/* Country Selector - Compact in header */}
              <div className="hidden md:block ml-4">
                <CountrySelector 
                  compact={true}
                  currentCountry={selectedCountry?.code || 'chile'}
                  onCountryChange={(country) => {
                    setSelectedCountry(country);
                    toast.success(`País cambiado a ${country.name} ${country.flag}`);
                  }}
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Profile Selector Button */}
              <button
                onClick={() => setShowProfileSelector(!showProfileSelector)}
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 rounded-lg text-indigo-400 hover:from-indigo-500/30 hover:to-purple-500/30 transition-all text-sm"
                data-testid="profile-selector-btn"
              >
                <span className="text-lg">{selectedProfile?.icon || '👤'}</span>
                <span className="hidden lg:inline">{selectedProfile?.name || 'Perfil'}</span>
              </button>

              {/* Premium Button */}
              <button
                onClick={() => setShowPlans(true)}
                data-testid="premium-btn"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-lg text-yellow-400 hover:from-yellow-500/30 hover:to-orange-500/30 transition-all text-sm"
              >
                <Crown className="w-4 h-4" />
                <span>Premium</span>
              </button>

              {/* Admin Button — visible only for admin */}
              {user?.is_admin || user?.email === 'mleroux@notariasanantonio.cl' ? (
                <Link
                  to="/admin"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 transition-colors text-sm"
                >
                  <Shield className="w-4 h-4" />
                  <span>Admin</span>
                </Link>
              ) : null}

              {/* Referrals Button */}
              <Link
                to="/referrals"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-400 hover:bg-purple-500/30 transition-colors text-sm"
                data-testid="referrals-btn"
              >
                <Gift className="w-4 h-4" />
                <span>Referidos</span>
              </Link>

              {/* Support Button */}
              <Link
                to="/support"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/20 border border-cyan-500/30 rounded-lg text-cyan-400 hover:bg-cyan-500/30 transition-colors text-sm"
                data-testid="support-btn"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Soporte</span>
              </Link>

              {/* AI Tools Button */}
              <Link
                to="/tools"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-red-500/20 to-purple-500/20 border border-red-500/30 rounded-lg text-red-400 hover:from-red-500/30 hover:to-purple-500/30 transition-colors text-sm"
                data-testid="ai-tools-btn"
              >
                <Sparkles className="w-4 h-4" />
                <span>IA Avanzada</span>
              </Link>

              {/* Theme Toggle */}
              <ThemeToggle className="hidden sm:block" />
              
              {/* Offline Indicator */}
              <OfflineIndicator />
              
              {/* Invitations Badge */}
              <InvitationBadge />
              
              {/* Alerts Button */}
              <button
                onClick={() => setShowAlerts(true)}
                data-testid="alerts-btn"
                className="relative p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Bell className="w-5 h-5" />
                {alertSummary?.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs rounded-full flex items-center justify-center">
                    {alertSummary.unread_count}
                  </span>
                )}
              </button>
              {/* Notification Settings Button */}
              <button
                onClick={() => setShowNotificationSettings(true)}
                data-testid="notification-settings-btn"
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                title="Configurar Notificaciones"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/seguridad')}
                data-testid="security-link"
                className="p-2 text-slate-400 hover:text-emerald-300 hover:bg-slate-800 rounded-lg transition-colors"
                title="Seguridad y Cumplimiento"
              >
                <ShieldCheck className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/mi-perfil')}
                data-testid="profile-link"
                className="text-sm text-slate-400 hover:text-white hidden sm:block"
                title="Mi Perfil"
              >
                {user?.username || user?.email}
              </button>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="flex items-center space-x-2 whitespace-nowrap text-slate-400 hover:text-white transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Profile Selector Modal */}
      {showProfileSelector && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowProfileSelector(false)}>
          <div className="w-full max-w-md bg-slate-900 rounded-2xl border border-slate-700 p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-white mb-4">Seleccionar Perfil</h2>
            <p className="text-gray-400 text-sm mb-4">Tu perfil determina los módulos y herramientas disponibles</p>
            <ProfileSelector 
              currentProfile={selectedProfile?.type}
              onProfileChange={(profile) => {
                setSelectedProfile(profile);
                setShowProfileSelector(false);
                toast.success(`Perfil cambiado a ${profile.name} ${profile.icon}`);
              }}
            />
            <button 
              onClick={() => setShowProfileSelector(false)}
              className="mt-4 w-full py-2 text-gray-400 hover:text-white transition-colors text-sm"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        {logoutError && <div data-testid="logout-error" role="alert" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 px-4 py-3 text-sm text-amber-200"><p data-testid="logout-error-message">{logoutError}</p><button data-testid="logout-retry" onClick={handleLogout} className="shrink-0 rounded-lg border border-amber-400/60 px-3 py-2 font-medium hover:bg-amber-500/10">Reintentar cierre de sesión</button></div>}
        <Link data-testid="dashboard-vocabulary-link" to="/vocabulario-juridico" className="mb-6 flex items-center justify-between gap-3 border-y border-emerald-800 py-4 text-emerald-300 transition-colors hover:text-emerald-100"><span className="font-semibold">Vocabulario jurídico</span><span className="text-sm">Conceptos y fuentes →</span></Link>
        {/* Judicial Intelligence featured card */}
        <Link
          to="/inteligencia-judicial"
          data-testid="judicial-intelligence-card"
          className="group relative block overflow-hidden rounded-2xl border border-amber-500/30 bg-slate-900 p-5 md:p-6 mb-6 md:mb-8 hover:border-amber-400/60 transition-colors"
        >
          <div className="absolute right-0 top-0 w-40 h-40 rounded-full bg-amber-500/10 blur-3xl" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
              <Gavel className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-400">Nuevo · Cerebro IA</span>
                <Brain className="w-4 h-4 text-amber-400" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white">Inteligencia Judicial</h3>
              <p className="text-sm text-slate-400">Perfiles de jueces y ministros en 6 países, tasas de victoria y estrategia recomendada para tu caso.</p>
            </div>
            <span
              data-testid="dashboard-experts-link"
              onClick={(e) => { e.preventDefault(); navigate('/expertos-notariales'); }}
              className="inline-flex items-center gap-2 self-start md:self-center rounded-full border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-slate-800 transition-colors"
            >
              Expertos notariales
            </span>
            <UrgentDocsCard />
            <span
              data-testid="dashboard-calendar-link"
              onClick={(e) => { e.preventDefault(); navigate('/calendario-plazos'); }}
              className="inline-flex items-center gap-2 self-start md:self-center rounded-full border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-slate-800 transition-colors"
            >
              Plazos
            </span>
            <span
              data-testid="dashboard-crm-link"
              onClick={(e) => { e.preventDefault(); navigate('/crm'); }}
              className="inline-flex items-center gap-2 self-start md:self-center rounded-full border border-amber-500/50 px-4 py-2 text-sm font-semibold text-amber-300 hover:bg-amber-500/10 transition-colors"
            >
              Mis casos (CRM)
            </span>
            <span className="inline-flex items-center gap-2 self-start md:self-center rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 group-hover:translate-x-1 transition-transform">
              Analizar jueces <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>

        <ProfileHubsSection profileType={selectedProfile?.type} />

        {/* Quick Actions - Mobile optimized */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <Link
            to="/discovery"
            data-testid="new-contract-btn"
            className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <Plus className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Nuevo Contrato</h3>
                <p className="text-xs md:text-sm text-blue-200 hidden md:block">Crear con asistente IA</p>
              </div>
            </div>
          </Link>

          <Link
            to="/recommend"
            data-testid="recommend-btn"
            className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <Sparkles className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">¿Qué Necesitas?</h3>
                <p className="text-xs md:text-sm text-purple-200 hidden md:block">IA recomienda contratos</p>
              </div>
            </div>
          </Link>

          <Link
            to="/templates"
            data-testid="templates-btn"
            className="bg-card border border-border rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center text-cyan-400 group-hover:bg-cyan-500/20 transition-colors">
                <LayoutTemplate className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Plantillas</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">198 contratos</p>
              </div>
            </div>
          </Link>

          <Link
            to="/my-templates"
            data-testid="my-templates-btn"
            className="bg-card border border-border rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-500/10 rounded-lg flex items-center justify-center text-purple-400 group-hover:bg-purple-500/20 transition-colors">
                <FileText className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Mis Plantillas</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">Personalizadas</p>
              </div>
            </div>
          </Link>

          <Link
            to="/legal-feed"
            data-testid="legal-feed-btn"
            className="bg-card border border-border rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-500/10 rounded-lg flex items-center justify-center text-amber-400 group-hover:bg-amber-500/20 transition-colors">
                <Newspaper className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Actualidad Jurídica</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">Noticias y actualizaciones</p>
              </div>
            </div>
          </Link>

          {/* Import Contract Button */}
          <button
            onClick={() => setShowImport(true)}
            data-testid="import-contract-btn"
            className="bg-card border border-dashed border-slate-600 hover:border-emerald-500/50 rounded-xl p-4 md:p-6 card-hover group text-left"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center text-emerald-400 group-hover:bg-emerald-500/20 transition-colors">
                <Upload className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Importar</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">PDF o Word</p>
              </div>
            </div>
          </button>

          {/* Contract Protection with AI Button - NEW */}
          <Link
            to="/proteger-contrato"
            data-testid="contract-protection-btn"
            className="bg-gradient-to-br from-purple-500/10 to-pink-600/10 border border-purple-500/30 rounded-xl p-4 md:p-6 card-hover group relative overflow-hidden"
          >
            <div className="absolute top-2 right-2">
              <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/30 text-purple-300 rounded-full font-medium">NUEVO</span>
            </div>
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 group-hover:bg-purple-500/30 transition-colors">
                <Shield className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Proteger Contrato</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">Análisis IA + Mejoras</p>
              </div>
            </div>
          </Link>

          {/* Contract Comparison Button - NEW */}
          <Link
            to="/comparar-contratos"
            data-testid="contract-comparison-btn"
            className="bg-gradient-to-br from-blue-500/10 to-purple-600/10 border border-blue-500/30 rounded-xl p-4 md:p-6 card-hover group relative overflow-hidden"
          >
            <div className="absolute top-2 right-2">
              <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/30 text-blue-300 rounded-full font-medium">NUEVO</span>
            </div>
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-500/20 rounded-lg flex items-center justify-center text-blue-400 group-hover:bg-blue-500/30 transition-colors">
                <ArrowLeftRight className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Comparar Contratos</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">¿Cuál firmar?</p>
              </div>
            </div>
          </Link>

          {/* Protection History Button */}
          <Link
            to="/historial-proteccion"
            data-testid="protection-history-btn"
            className="bg-gradient-to-br from-amber-500/10 to-orange-600/10 border border-amber-500/30 rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-500/20 rounded-lg flex items-center justify-center text-amber-400 group-hover:bg-amber-500/30 transition-colors">
                <History className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Historial</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">Análisis anteriores</p>
              </div>
            </div>
          </Link>

          {/* Legal Triage - Notification Assistant - NEW */}
          <Link
            to="/consejero-legal"
            data-testid="legal-triage-btn"
            className="bg-gradient-to-br from-red-500/10 to-orange-600/10 border border-red-500/30 rounded-xl p-4 md:p-6 card-hover group relative overflow-hidden"
          >
            <div className="absolute top-2 right-2">
              <span className="text-[10px] px-1.5 py-0.5 bg-red-500/30 text-red-300 rounded-full font-medium">NUEVO</span>
            </div>
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-red-500/20 rounded-lg flex items-center justify-center text-red-400 group-hover:bg-red-500/30 transition-colors">
                <AlertCircle className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Consejero Legal</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">Analizar notificación</p>
              </div>
            </div>
          </Link>

          {/* Accounting AI Button */}
          <Link
            to="/contabilidad"
            data-testid="accounting-ai-btn"
            className="bg-gradient-to-br from-green-500/10 to-emerald-600/10 border border-green-500/30 rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-green-500/20 rounded-lg flex items-center justify-center text-green-400 group-hover:bg-green-500/30 transition-colors">
                <Calculator className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Contabilidad IA</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">Balance y tributación</p>
              </div>
            </div>
          </Link>

          {/* Legal Deadlines Calculator */}
          <Link
            to="/plazos-legales"
            data-testid="legal-deadlines-btn"
            className="bg-gradient-to-br from-indigo-500/10 to-purple-600/10 border border-indigo-500/30 rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center text-indigo-400 group-hover:bg-indigo-500/30 transition-colors">
                <Scale className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Plazos Legales</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">Calculadora judicial</p>
              </div>
            </div>
          </Link>

          {/* Labor Calculator */}
          <Link
            to="/calculadora-laboral"
            data-testid="labor-calculator-btn"
            className="bg-gradient-to-br from-teal-500/10 to-emerald-600/10 border border-teal-500/30 rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-500/20 rounded-lg flex items-center justify-center text-teal-400 group-hover:bg-teal-500/30 transition-colors">
                <Briefcase className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Calculadora Laboral</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">Finiquitos e indemnizaciones</p>
              </div>
            </div>
          </Link>

          {/* Jurisprudence Search */}
          <Link
            to="/jurisprudencia"
            data-testid="jurisprudence-btn"
            className="bg-gradient-to-br from-purple-500/10 to-pink-600/10 border border-purple-500/30 rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 group-hover:bg-purple-500/30 transition-colors">
                <Gavel className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Jurisprudencia</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">Búsqueda de sentencias</p>
              </div>
            </div>
          </Link>

          {/* Legal Documents Generator */}
          <Link
            to="/escritos-legales"
            data-testid="legal-docs-btn"
            className="bg-gradient-to-br from-rose-500/10 to-orange-600/10 border border-rose-500/30 rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-rose-500/20 rounded-lg flex items-center justify-center text-rose-400 group-hover:bg-rose-500/30 transition-colors">
                <BookOpen className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Escritos Judiciales</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">Generador de demandas</p>
              </div>
            </div>
          </Link>

          {/* Poder Judicial */}
          <Link
            to="/poder-judicial"
            data-testid="poder-judicial-btn"
            className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/30 rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center text-cyan-400 group-hover:bg-cyan-500/30 transition-colors">
                <Building2 className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Poder Judicial</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">Consulta de causas</p>
              </div>
            </div>
          </Link>

          {/* Electronic Signature */}
          <Link
            to="/firma-electronica"
            data-testid="firma-electronica-btn"
            className="bg-gradient-to-br from-violet-500/10 to-purple-600/10 border border-violet-500/30 rounded-xl p-4 md:p-6 card-hover group"
          >
            <div className="flex flex-col md:flex-row items-center md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-violet-500/20 rounded-lg flex items-center justify-center text-violet-400 group-hover:bg-violet-500/30 transition-colors">
                <PenTool className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-semibold font-['Outfit'] text-sm md:text-lg">Firma Electrónica</h3>
                <p className="text-xs md:text-sm text-slate-400 hidden md:block">FEA - Ley 19.799</p>
              </div>
            </div>
          </Link>
        </div>

        {/* Enhanced Charts Toggle and Section */}
        <div className="mt-6 mb-2 flex items-center justify-between">
          <h2 className="text-lg font-semibold font-['Outfit'] flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-cyan-400" />
            <span>Tus Estadísticas</span>
          </h2>
          <button
            onClick={() => setShowEnhancedCharts(!showEnhancedCharts)}
            className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors text-sm"
            data-testid="toggle-enhanced-charts"
          >
            <BarChart3 className="w-4 h-4" />
            <span>{showEnhancedCharts ? 'Vista simple' : 'Ver métricas avanzadas'}</span>
          </button>
        </div>
        <p className="text-sm text-slate-400 mb-4">Resumen de tu actividad legal</p>

        {showEnhancedCharts ? (
          <EnhancedDashboardCharts stats={dashboardStats} contracts={contracts} />
        ) : (
          <>
            {/* Analytics Dashboard */}
            <DashboardAnalytics />
          </>
        )}

        {/* Deadlines Widget */}
        <div className="my-6">
          <DeadlinesWidget />
        </div>

        {/* Usage Dashboard (for free plan) */}
        <div className="mb-6">
          <UsageDashboard compact={true} />
        </div>

        {/* Personalized Recommendations */}
        <PersonalizedRecommendations />

        {/* Contracts List */}
        <div className="bg-card border border-border rounded-xl">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold font-['Outfit'] flex items-center space-x-2">
                <FileText className="w-5 h-5 text-blue-400" />
                <span>Mis Contratos</span>
              </h2>
              {dataSource === 'cache' && (
                <span className="flex items-center gap-1.5 text-xs text-yellow-400 bg-yellow-400/10 px-2 py-1 rounded-full">
                  <CloudOff className="w-3 h-3" />
                  Datos en caché
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-4" />
              <p className="text-slate-400">Cargando contratos...</p>
            </div>
          ) : contracts.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-600 mb-4" />
              <h3 className="text-lg font-medium mb-2">No tienes contratos aún</h3>
              <p className="text-slate-400 mb-6">Crea tu primer contrato con nuestro asistente inteligente</p>
              <Link
                to="/discovery"
                data-testid="empty-new-contract-btn"
                className="btn-primary px-6 py-2 inline-flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Crear Contrato</span>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {contracts.map((contract) => (
                <Link
                  key={contract.id}
                  to={`/contract/${contract.id}`}
                  data-testid={`contract-item-${contract.id}`}
                  className="flex items-center justify-between p-4 hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                      {contract.is_signed ? (
                        <PenTool className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <FileText className="w-5 h-5 text-blue-400" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium">{contract.title}</h3>
                      <p className="text-sm text-slate-400">
                        {new Date(contract.created_at).toLocaleDateString('es-CL')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {getConflictScoreBadge(contract.conflict_score)}
                    {getStatusBadge(contract)}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Chat Assistant */}
      <LegalChatAssistant />
    </div>
  );
};

export default DashboardPage;
